import heroHomebuyerImg from "@/assets/guide/hero-homebuyer.jpg";
import financesPlanningImg from "@/assets/guide/finances-planning.jpg";
import mortgageOptionsImg from "@/assets/guide/mortgage-options.jpg";
import creditScoreImg from "@/assets/guide/credit-score.jpg";
import houseHuntingImg from "@/assets/guide/house-hunting.jpg";
import negotiationImg from "@/assets/guide/negotiation.jpg";
import closingDayImg from "@/assets/guide/closing-day.jpg";
import first90DaysImg from "@/assets/guide/first-90-days.jpg";
import homeownerFirstYearImg from "@/assets/guide/homeowner-first-year.jpg";
import creditRunwayImg from "@/assets/guide/credit-runway.jpg";

export interface GuideSection {
  heading: string;
  content: string;
  /** Optional tip/callout box */
  tip?: string;
  /** Optional image for this section */
  image?: string;
  /** Alt text for the image */
  imageAlt?: string;
}

export interface GuideFAQ {
  question: string;
  answer: string;
}

export interface Guide {
  slug: string;
  title: string;
  subtitle: string;
  author: string;
  publishDate: string;
  readTime: string;
  category: string;
  heroImage?: string;
  heroImageSet?: {
    webp400: string;
    webp800: string;
    width: number;
    height: number;
  };
  /** Number of sections visible before the gate */
  freePreviewSections: number;
  sections: GuideSection[];
  faqs: GuideFAQ[];
  ctaHeadline: string;
  ctaDescription: string;
}

export const guides: Guide[] = [
  {
    slug: "first-time-homebuyer-complete-guide",
    title: "The Definitive First-Time Homebuyer's Guide",
    subtitle:
      "How to close on your first property and accelerate your journey to financial freedom.",
    author: "Home Approach Team",
    publishDate: "February 2026",
    readTime: "35 min read",
    category: "Homebuying",
    heroImage: heroHomebuyerImg,
    heroImageSet: {
      webp400: "https://www.homeapproach.com/__l5e/assets-v1/f038a1d7-4035-4705-a25d-ef9a62d5318b/first-time-homebuyer-800.webp",
      webp800: "https://www.homeapproach.com/__l5e/assets-v1/f038a1d7-4035-4705-a25d-ef9a62d5318b/first-time-homebuyer-800.webp",
      width: 1600,
      height: 900,
    },
    freePreviewSections: 3,
    ctaHeadline: "Unlock the Full Guide — Free",
    ctaDescription:
      "Enter your email to instantly access all chapters, plus get our exclusive Homebuyer Checklist delivered to your inbox.",
    sections: [
      {
        heading: "Introduction: Why Homeownership Matters",
        image: heroHomebuyerImg,
        imageAlt: "Happy couple holding keys in front of their new home",
        content: `So, you're thinking about buying a home for the first time. That's great news! From increased financial security and tax benefits to having a permanent roof over your head and being part of a local community, there's a lot to like about becoming a homeowner.

If you're looking to achieve long-term financial independence, buying a home is one of the smartest things you can do. According to a recent study from the Federal Reserve, U.S. homeowners have a median net worth of $255,000. Renters, on the other hand, have a median net worth of just $6,300 — a difference of 40x!

While the benefits of being a homeowner speak for themselves, the process of buying your first home isn't exactly a walk in the park. Truth be told, the experience can be downright crazy and filled with emotional ups and downs.

To make your journey easier, we've put together this comprehensive guide that outlines everything you need to know about becoming a first-time homebuyer, including how to finance your first home, the pros and cons of working with a real estate agent, what to look for in a property, tips on negotiating a deal, what to expect after an offer is accepted, hidden homeowner costs to consider, unforeseen challenges you might encounter, first-time homebuyer mistakes to avoid, and additional resources that can help you throughout the process.`,
        tip: "Disclaimer: Owning a home is not for everyone. If you've got a nomadic lifestyle, don't have steady income, or are planning to relocate frequently, you may want to continue renting until you're ready to settle down.",
      },
      {
        heading: "Finances & Down Payment: What You Really Need",
        image: financesPlanningImg,
        imageAlt: "Financial planning with calculator, savings, and house keys",
        content: `Whether you're single, married, or in a relationship, working a full-time W2 job or earning income as a 1099 contractor, buying young or later on in life, there isn't one standard path to homeownership.

Wrapping your head around homeownership costs is not easy. Not only do you have to come up with a lump sum of cash for a down payment, you also have to manage monthly mortgage payments, property taxes, homeowners insurance, maintenance and repairs, and other costs that come with owning a home. This is the part where most first-time homebuyers feel overwhelmed, which is perfectly normal.

Reality check: A $500/month higher payment often feels scarier than it actually is — until you compare it to rent increases, tax deductions, and equity growth. Homeowners are now 43x wealthier than renters!

Whether you're buying a house for $2 million or $400,000, the old-school way of thinking is that you'd need to cough up at least 20% of the purchase price for a down payment. That's how you avoid paying for private mortgage insurance (PMI). On the flipside, some folks believe it's better to put down less money because it raises your return on investment.

In today's tricky real estate market, the cost of waiting until you've saved up 20% can be prohibitively expensive in the long run, so it may make sense to put less down, move in, and start building equity sooner rather than later.

"Whether you put down 20%, 30%, or just 3%, the most important thing is that you're stepping into homeownership," says Randy Nemeroff, founder of Hero Benefits Alliance. "It's more than an investment. It's a foundation for your future."`,
        tip: "Use this decision framework: High income + low savings → 3–5% down plus reserves. Stable savings + risk-averse → 20% down. Rising rents + stable job → Buy sooner, put less down. Volatile income → Delay while you build reserves.\n\n👉 [Use our Affordability Calculator](/mortgage-calculators) to see what monthly payment fits your budget.",
      },
      {
        heading: "Tips for Saving for a Down Payment",
        content: `Saving up for a down payment can be a massive undertaking for first-time homebuyers. Here are some tips to make the process easier.

Set a goal: You need to figure out how much you need to save up to begin with. The smart play is to have enough cash that you can put 20% down while still being able to afford closing costs and living costs after that. Study your finances, create a budget, determine what your ultimate goal is, and develop a plan that helps you get there.

Cut unnecessary spending: Once you've figured out how much you need to save, it's time to trim the fat off your budget. For example, you might not need subscriptions to Hulu, Netflix, and AppleTV all at the same time. Instead of going out for dinner several times a week, you might be able to save money by cooking more meals at home.

Optimize your savings: If your money is going to be parked away in an account, you might as well get the biggest return on it. Rather than putting your money in a regular savings account that generates paltry interest, consider a high-yield savings account (HYSA) instead.

Set up automatic deposits: If you're serious about saving for a down payment, consider creating a new bank account and automatically routing 5% or 10% of each paycheck there. That way, you get the peace of mind that comes with knowing you're building up your down payment without having to manually move money.

Pocket any windfalls: Win the lottery? Inherit some money? Get a huge bonus at work? Any money you receive from windfalls like these should automatically be routed to the account you're stockpiling your down payment in.`,
        tip: "👉 [Run your Stack](/grant-finder) to discover down payment assistance programs you may qualify for based on your location and profession.",
      },
      {
        heading: "Popular Mortgage Options for First-Time Homebuyers",
        image: mortgageOptionsImg,
        imageAlt: "Mortgage documents and model houses on a desk",
        content: `Assuming you don't have enough money to buy your first home with cash, you're going to need to secure a mortgage. In 2024, 91% of first-time homebuyers used mortgages to start their homeownership journeys.

FHA Loans: If you're a first-time homebuyer who has a debt-to-income ratio of 50% or less and a credit score of at least 580, you may be able to afford a home by putting down just 3.5%. For cash-strapped borrowers and folks with suboptimal credit scores, FHA loans are much easier to secure and can provide a path toward homeownership. But you'll have to pay PMI, and the FHA won't let you borrow a large amount of money.

Conventional Mortgages: If you find yourself on solid financial ground, a conventional mortgage may be a better option — particularly if you're able to put 20% down and have a credit score higher than 740. You may still qualify as long as your credit score is at least 620 and you can put down at least 10%.

Fixed-Rate Mortgages: A fixed-rate mortgage has the same interest rate throughout the life of the loan. Due to their predictable nature, many first-time homebuyers prefer them.

Variable-Rate Mortgages (ARMs): An adjustable-rate mortgage has interest rates that are fixed for the first few years but change over time based on market benchmarks. If you're planning on living at a property for just a couple of years, a 5/1 ARM could be a good option. If you're looking for a longer-term home, go with a fixed mortgage.

"For many homebuyers, nothing beats the stability of a conventional mortgage," says Randy Nemeroff of Hero Benefits Alliance. "With predictable terms and flexibility, it's the tried-and-true option for buyers who want to build long-term equity."`,
        tip: "If you plan to stay less than 5 years, an ARM may make sense. If you plan to stay at least 7 years, a fixed-rate mortgage almost always wins.\n\n👉 [Explore our Mortgage Glossary](/mortgage-glossary) to decode terms like ARM, PMI, and escrow.",
      },
      {
        heading: "Credit Scores: What You Need to Know",
        image: creditScoreImg,
        imageAlt: "Phone showing a 750 credit score with financial charts",
        content: `To get the best mortgage rate, you need to put down at least 20%, have a low debt-to-income ratio, and have a strong record of employment. On top of that, you need a solid credit score — typically at least 740 for the most favorable terms.

Your credit score is determined by five categories:

1. Payment history (35%) — how likely you are to repay debts on time.
2. Amount owed (30%) — your credit utilization rate. Below 10% but higher than 0% is ideal.
3. Credit history (15%) — the average age of all your credit accounts. Keep your oldest accounts open!
4. Credit mix (10%) — the different types of credit accounts you have.
5. Credit inquiries (10%) — how often you've opened new credit lines recently.

How to improve your score: Monitor your credit regularly using free sites like Credit Karma. Pay off credit cards on time and don't carry a balance. Stop applying for new credit (except your mortgage!). Keep older credit cards open, even if you barely use them.

"Your credit score doesn't define who you are, but it can hold the key to your financial future," explains Gordon Oliver, Director of Business Development for Cambridge Credit Counseling. "If your credit score isn't where you hope it would be, just remember you're not on your own."`,
        tip: "👉 [Take our Credit Readiness Quiz](/homebuying-support) to get a personalized action plan based on your current score range.",
      },
      {
        heading: "The Mortgage Process: From Pre-Approval to Underwriting",
        content: `In one word? Frustrating. You have to submit reams of documents over and over again, fill out countless forms, and chat with your broker repeatedly. Luckily, brokers are generally super-cool professionals who genuinely want to help you.

In today's competitive housing market, you need to be ready to pounce on a property. The easiest way to do that is by getting pre-approved. As you begin, determine how much money you can afford and what type of mortgage makes sense. Then collect documentation: W2 forms, 1099s, profit and loss statements, bank statements, investment account statements, and debt information.

By securing mortgage pre-approval, you demonstrate that you're a serious buyer who's ready to make a deal.

After you've been pre-approved and had an offer accepted, it's time to put down "earnest money" — typically 1–2% of the purchase price. Then you'll secure your actual mortgage, run a title search, conduct an inspection, and get the house appraised.

Shop around! If you buy a home for $500,000, put 20% down, and secure a 30-year fixed mortgage at 4%, you will pay $687,478 over the life of your loan. That same deal at 3.75% lowers your total payment to $666,886 — a savings of more than $20,000. Every decimal counts.

"The mortgage process can feel overwhelming, but it doesn't have to be," says Randy Nemeroff. "On average, it takes about 30 to 45 days from application to closing."`,
        tip: "Consider securing a rate lock once you find a good rate — it gives you peace of mind that your interest rate won't change over a determined period of time (30, 45, or 60 days).\n\n👉 [Run the numbers with our Mortgage Calculators](/mortgage-calculators) to compare scenarios.",
      },
      {
        heading: "Tax Credits & Closing Costs",
        content: `As a first-time homebuyer, you may qualify for a tax credit when you close on a new home. Keep your eyes peeled for potential tax credits from both your state and federal governments.

It's also important to be familiar with the tax advantages inherent in homeownership. All mortgage interest is usually tax deductible. In 2026, homeowners can also write off up to $40,400 for state and local taxes (including property tax).

Closing costs are the first-time homebuyer's often-overlooked financial enemy. Get ready to be hit by a deluge of fees including:

• Loan application fees • Attorney fees • Closing costs paid to the title company • Courier fees • Escrow deposits • Homeowners insurance (first year upfront) • Mortgage broker fees (0.5%–2.75%) • Title insurance • Origination fees (~1%) • Recording fees (~$125) • Title search fees ($200–$400) • Mansion taxes where applicable

Very broadly, closing costs range between 2% and 5% of your mortgage. So if you're taking out a $500,000 loan, you might be on the hook for an additional $25,000 in closing costs, or even more.`,
        tip: "Just when you think you've wrapped your head around how much your first house will cost, more fees will almost certainly come your way. Be ready.",
      },
      {
        heading: "Real Estate Agents: Pros & Cons",
        image: houseHuntingImg,
        imageAlt: "Couple touring a home with a real estate agent",
        content: `According to the National Association of Realtors®, 87% of recent homebuyers enlisted the services of a real estate agent. But not every first-time homebuyer needs to hire an agent.

Advantages of working with a realtor:

Faster process — You leverage the experience of someone who lives and breathes the process daily.

Market knowledge — The right agent knows the local market inside and out and can help you identify reasonably priced properties.

Negotiation skills — An agent negotiates the deal on your behalf, which can help you get a better price.

Networking — Agents know home inspectors, attorneys, and other professionals you'll need.

Potential extra savings — You might qualify for programs like [Hero Rewards](/hero-rewards), which rewards heroes like military veterans, law enforcement, firefighters, teachers, and healthcare providers with an average of $5,000+ back at closing.

Disadvantages of working with a realtor:

Commission — Historically 5–6% of the deal price, split between buying and selling agents. As of August 2024, new rules give sellers the option to cover costs while making buyers responsible for their own agent's commission.

Intermediary — You can't directly communicate with the other side of the deal.

Multiple clients — Your agent has other clients, which means you might have to wait.

Misalignment — 73% of buyers only interview one agent. If you end up with the wrong one, they may lead you down a path that's not in your best interest.`,
        tip: "You can (and should) try to negotiate down the length of exclusivity contracts just in case you aren't happy with your agent's representation.",
      },
      {
        heading: "What to Think About When Buying Your First Home",
        content: `Beyond the house itself, here are critical considerations to keep in mind:

Neighborhood — You're buying more than just a house. Is your ideal home within walking distance of restaurants? Or would you prefer open space? Spend time studying the neighborhood. Avoid buying the most expensive house in the neighborhood — it could hurt you down the road. Talk to would-be neighbors and check area crime stats.

Schools — Even if you don't have kids, education is highly correlated with property values. According to the National Bureau of Economic Research, property values increase $20 for every $1 spent on education.

Property taxes — Wrap your mind around local property taxes and how they've changed over time.

Location — Are you happy living in the sticks or do you prefer being close to public transportation and amenities?

Town politics — Research the town's politics and finances to avoid areas involved in scandals or lawsuits.

Starter home vs. forever home — If you're buying a starter home, you can defer capital gains when you buy your next home using a 1031 exchange.

Co-ops and condos — These have additional regulations and bylaws. You might need board approval, and there may be regulations governing renovations, pets, and noise.

Flood zones — Pay close attention to flood zone risk. Depending on where a property lies, you may have to buy flood insurance, which costs an average of $888 per year.`,
      },
      {
        heading: "Making a Competitive Offer",
        image: negotiationImg,
        imageAlt: "Handshake over real estate documents and house keys",
        content: `After falling in love with a house, you might be ready to make an offer. But what if other buyers are interested in the same property? Here's how to make your offer as competitive as possible:

1. Get pre-approved — This shows sellers you're serious and financially capable.

2. Make a good offer — In a competitive market, coming in too low means losing out. But don't let emotions force you to overbid. The right agent will know how much a home is worth.

3. Be flexible — If you can, be super flexible about closing dates. Some sellers might consider a slightly lower offer if it means they have extra time to move out.

4. Consider an escalation clause — This allows your offer to automatically increase if another buyer comes in with a higher offer, up to a maximum price you set. It's a smart way to avoid bidding wars while sticking within your budget.

5. Write a personal letter — Selling a house is emotional. A letter sharing why you love their home can help a seller feel confident their home will end up in good hands. Keep it honest and concise.

Once your offer is accepted, you may need to put down 10% into escrow. Then it's time to hire a home inspector who will examine the HVAC system, furnace, structural components, electrical systems, plumbing, roof, and chimney. Inspections cost $300–$1,000 on average.`,
        tip: "In certain situations, writing a brief personal story of who you are and why you want the house can make your offer stand out — especially in a seller's market.",
      },
      {
        heading: "Common Forks in the Road",
        content: `Every step of buying your first home is a balancing act between what your heart wants, what your wallet can handle, and what the market is telling you. Here are three common decision points:

Should I buy now or wait? It's normal to feel torn. Emotionally, you might feel ready. Financially, you might wonder if waiting means a better deal. Remember: buying a home isn't a race. Focus on what you can control — building a solid financial base, getting pre-approved, and being patient for the right opportunity.

Should I stretch my budget or play it safe? It's easy to fall in love with a house just slightly outside your price range. But owning a home is a huge responsibility. When you stretch your budget, you might feel financial strain down the line — "house-poor." Instead, look for a home that gives you financial breathing room. Your first home doesn't have to be your forever home.

Should I walk away from this house? Maybe something feels off. The seller is sending weird signals, the inspection turned up something sketchy, or your attorney surfaced an issue. If the financial picture isn't adding up, walking away might be the smartest move. There are countless other houses out there. Trust your instincts.`,
        tip: "\"Love at first sight\" can be deceptive in real estate. Try not to get too emotionally attached until the details check out.",
      },
      {
        heading: "Hidden Costs & Your First 90 Days",
        image: first90DaysImg,
        imageAlt: "Young homeowners unpacking in their new home on moving day",
        content: `Being a homeowner is not an inexpensive endeavor. Here are hidden costs to consider:

• Additional taxes (fire district taxes, etc.) • Homeowners insurance (~$2,424/year for $300K coverage) • Higher utility bills in a larger space • Inevitable repairs (AC, furnace, water treatment) • Moving costs ($800–$5,700 depending on distance) • Time off of work for moving

The cheapest house often becomes the most expensive over time.

Your first 90 days after closing are all about settling in, organizing your finances, and making sure everything's in good condition.

Immediately after closing: Change the locks on all exterior doors and set up utilities in your name. Walk around and familiarize yourself with important systems like your HVAC and electrical panel. Collect all important documents in a safe place.

Budget reset: Revisit your monthly budget to account for your new mortgage payment, property taxes, insurance, and estimated utility costs. Set aside 1–2% of your home's value annually for maintenance.

Build your emergency fund: A solid cash reserve covering 3–6 months of housing expenses will protect you against unexpected costs.

Home maintenance: Schedule routine inspections for your HVAC, roof, and plumbing. Keep a home maintenance calendar.`,
        tip: "Before you start any renovations, live in your home for a few months first. You'll get a better sense of what actually needs to change.\n\n👉 [Use our Homebuyer Checklist](/checklist) to stay organized through the entire process.",
      },
      {
        heading: "First-Time Homebuyer Mistakes to Avoid",
        image: closingDayImg,
        imageAlt: "Hands exchanging the house key at the closing table",
        content: `Since they've never navigated the process before, first-time homebuyers commonly make these mistakes:

Finding a house before securing a mortgage — Without pre-approval, it's impossible to act fast. In today's competitive market, failing to secure financing before shopping means you won't be first to act.

Not shopping mortgage brokers — Many first-time homebuyers choose the first broker they talk to. By shopping around, you may get a better rate. Over a 30-year loan, a fraction of a percent makes a huge difference.

Not doing an inspection — Some first-time buyers bypass an inspection because they've fallen in love with the property. A few months later, they learn the hard way why inspections are necessary. Inspections are always necessary. Skip one at your own risk.

Spending more than you should — Being able to live comfortably on the other side of your first transaction is critical. Don't take on a bigger property than you can truly afford.

Making decisions based on emotion — Many first-time homebuyers let emotion guide their decision-making. Working with a trusted real estate agent can provide an experienced, knowledgeable, and objective lens.

Hard truth: The first home you buy is rarely your forever home, and that's okay.`,
      },
      {
        heading: "Grants, Resources & Your Next Steps",
        content: `Homebuyer grants can be a great way to reduce your upfront costs. Since these programs change frequently, check with your state government housing agency for the latest opportunities.

Down payment help most buyers never hear about:

Ladder Up Home Loan Grants — If you qualify for City National Bank's program (available in qualifying markets in NY, NJ, and CA), you can put as little as 3% down without PMI, and may qualify for a grant of up to $20,000.

[Hero Rewards](/hero-rewards) — You could qualify for up to $5,000 cash back at closing if you're a military service member, law enforcement officer, firefighter, EMS technician, educator, healthcare provider, government employee, nonprofit professional, or union member.

Additional resources:
• USA.gov | Help Buying a New Home
• NACA | Best In America Mortgage
• Bankrate | 5 First-time Homebuyer Loans and Programs
• NerdWallet | First-Time Home Buyer Programs by State
• Freddie Mac | Three Pro Tips for First-Time Homebuyers

At Home Approach, we're here to guide you every step of the way. We connect you with trusted real estate and lending partners experienced in working with first-time buyers — and in many cases, our partners can offer added financial rewards or benefits.

Wherever you are in your journey, we're ready to help you take the next step with confidence. Here's to landing the home of your dreams!`,
        tip: "👉 Ready to start? [Explore our Homebuying Support Hub](/homebuying-support) or [Find an Expert](/experts) to guide you through every step.",
      },
    ],
    faqs: [
      {
        question: "How much money do I need to buy my first home?",
        answer: "You don't necessarily need 20% down. FHA loans allow as little as 3.5% down with a credit score of 580+. Some conventional programs accept 3% down. Factor in closing costs (2–5% of the loan), moving expenses, and a 3–6 month emergency fund. Use our Mortgage Calculators to estimate your total costs.",
      },
      {
        question: "What credit score do I need to buy a house?",
        answer: "The minimum depends on loan type: FHA loans require 580 for 3.5% down (or 500 with 10% down), conventional loans typically need 620+, and VA/USDA loans usually require 620+. For the best rates, aim for 740 or higher. You can check your readiness with our Credit Readiness Quiz.",
      },
      {
        question: "What's the difference between pre-qualification and pre-approval?",
        answer: "Pre-qualification is a quick, informal estimate based on self-reported finances — it carries little weight with sellers. Pre-approval involves a full credit check and document review, resulting in a conditional commitment from a lender. Always get pre-approved before house hunting.",
      },
      {
        question: "Should I buy a house or keep renting?",
        answer: "Homeowners have a median net worth 40x higher than renters ($255,000 vs. $6,300). If you have stable income, plan to stay 5+ years, and can afford monthly payments without being house-poor, buying is usually the better long-term move. If your income is volatile or you plan to relocate soon, renting may make more sense.",
      },
      {
        question: "What are closing costs and how much should I expect?",
        answer: "Closing costs typically range from 2–5% of your mortgage amount and include lender fees, title insurance, attorney fees, escrow deposits, and homeowners insurance. On a $400,000 loan, expect $8,000–$20,000. Some of these costs are negotiable, and certain grants or programs can help offset them.",
      },
      {
        question: "Do I need a real estate agent to buy a home?",
        answer: "While not required, 87% of buyers use an agent. A good buyer's agent provides market expertise, negotiation skills, and access to off-market listings. If you're a teacher, veteran, healthcare worker, or first responder, you may qualify for cash-back rewards through homeapproach.com.",
      },
      {
        question: "What first-time homebuyer programs am I eligible for?",
        answer: "Programs vary by state and profession. FHA loans, VA loans (for veterans), USDA loans (rural areas), and state-specific down payment assistance grants are common options. Use Stack to discover programs based on your location, profession, and financial situation.",
      },
      {
        question: "How long does it take to buy a house from start to finish?",
        answer: "The typical timeline is 3–6 months: 1–2 months for saving and pre-approval, 1–2 months for house hunting, and 30–45 days from accepted offer to closing. In competitive markets, the process can take longer. Being pre-approved and financially prepared helps you move faster.",
      },
      {
        question: "What hidden costs should I budget for as a new homeowner?",
        answer: "Beyond mortgage payments, budget for property taxes, homeowners insurance (~$2,424/year), HOA fees (if applicable), maintenance (1–2% of home value annually), higher utility bills, and unexpected repairs. The cheapest house often becomes the most expensive over time if maintenance has been deferred.",
      },
      {
        question: "What's the biggest mistake first-time homebuyers make?",
        answer: "The most common mistake is not getting pre-approved before house hunting, which means you can't act fast in competitive markets. Other costly mistakes include skipping the home inspection, stretching beyond your budget, making emotional decisions, and not shopping around for mortgage rates — even 0.25% can save tens of thousands over a 30-year loan.",
      },
    ],
  },
  {
    slug: "homeowner-first-year-playbook",
    title: "The Homeowner's First-Year Playbook",
    subtitle:
      "A month-by-month plan for your first 12 months of ownership — paperwork, maintenance, taxes, and equity.",
    author: "Home Approach Team",
    publishDate: "July 2026",
    readTime: "25 min read",
    category: "Homeownership",
    heroImage: homeownerFirstYearImg,
    freePreviewSections: 3,
    ctaHeadline: "Get the Full First-Year Playbook",
    ctaDescription:
      "Every checklist, template, and reminder for your first year as a homeowner — free.",
    sections: [
      {
        heading: "Week 1: Secure the House and the Paperwork",
        image: homeownerFirstYearImg,
        imageAlt: "New homeowner unpacking in a sunlit living room",
        content: `Closing day is the finish line for the transaction — and the starting line for ownership. The first week is about locking things down before life gets busy.

Change the locks. You have no idea how many copies of the old keys are floating around with contractors, dog walkers, and past guests. Rekey every exterior door or swap in smart locks the day you get the keys.

Transfer utilities into your name. Water, electric, gas, internet, and trash pickup. Set the transfer date to the day of closing so nothing lapses.

Locate the shutoffs. Main water valve, gas shutoff, electrical panel, and any sub-panels. Take photos and label them. In an emergency you will not want to be reading the inspection report.

File your closing packet. Scan the HUD-1 / Closing Disclosure, deed, title insurance policy, and inspection report into cloud storage. You will need every one of these at tax time and when you refinance or sell.`,
        tip: "Rekeying costs $80–$150 per lock. Skipping it is the single most common first-week mistake homeowners regret.",
      },
      {
        heading: "Month 1: Reset Your Budget for Real Ownership Costs",
        content: `The mortgage payment is only part of the number. In month one, rebuild your budget to match the reality of ownership.

Recurring costs to line-item: principal + interest, property taxes (if not escrowed), homeowners insurance, HOA dues, utilities in a larger footprint, lawn care or snow removal, and pest control.

Reserve funds to open: a maintenance reserve funded at 1–2% of the home's value per year, and a separate cash-in-hand emergency fund covering 3–6 months of housing costs. Automate weekly transfers so it happens without willpower.

Insurance walkthrough: re-read your homeowners policy. Confirm dwelling coverage matches rebuild cost, not market value. Add flood or earthquake riders if your area needs them — standard policies exclude both.`,
        tip: "👉 [Run the numbers with our Mortgage Calculators](/mortgage-calculators) to line up your new monthly outflow.",
      },
      {
        heading: "Months 2–3: Systems, Warranties, and the Maintenance Calendar",
        content: `Now that you've lived in the home a few weeks, you know how it actually behaves. Time to get ahead of the small stuff before it becomes the big stuff.

Build a maintenance calendar. Quarterly: HVAC filter swaps, smoke and CO detector tests, gutter check. Twice a year: HVAC service, water heater flush, dryer vent cleaning. Annually: roof inspection, tree trimming, sump pump test, chimney sweep if applicable.

Register warranties. Every major appliance and the HVAC system should be registered with the manufacturer. It takes 10 minutes each and can save thousands on a covered failure.

Meet a handyman before you need one. Find a general handyman, a plumber, an HVAC tech, and an electrician in your area. Get one small non-urgent job done with each so you have a relationship established before an emergency.`,
      },
      {
        heading: "Months 4–6: Taxes, Escrow, and the Homestead Exemption",
        content: `Homeownership unlocks deductions and exemptions most people never claim. Do this work in the first half of the year while you still have room in the calendar.

File your homestead exemption. In most states, filing a homestead exemption on your primary residence lowers your property tax bill by 10–25% every year for as long as you own the home. Deadlines vary by state — search "[your state] homestead exemption deadline" and file immediately.

Track deductible costs. Mortgage interest, property taxes (up to the SALT cap), and points paid at closing are deductible if you itemize. Any energy-efficient upgrades — heat pumps, insulation, solar, EV chargers — may qualify for federal tax credits.

Escrow reconciliation. Around the 6-month mark, your lender will run an escrow analysis. If taxes or insurance came in higher than projected, your payment will jump. Read the analysis, don't just accept it — mistakes happen.`,
        tip: "The homestead exemption is free money most first-year homeowners forget. Set a phone reminder for the filing deadline the day you close.",
      },
      {
        heading: "Months 7–9: Build Equity Faster (Without House-Poor Stress)",
        content: `Now that the essentials are handled, look for the equity levers that pay off for decades.

Make one extra principal payment per year. On a 30-year mortgage, one extra payment annually cuts about 4 years off the loan and tens of thousands in interest. You can also split each payment biweekly for the same effect.

Watch refinance thresholds. As a rule of thumb, refinancing is worth exploring when current rates are 0.75%+ below your rate and you plan to stay 3+ years. Run a break-even calculation before you pull the trigger — closing costs matter.

Do value-add work, skip vanity work. Kitchen updates, bath refreshes, and curb appeal reliably return 60–80% at resale. Pools, elaborate landscaping, and over-personalized finishes rarely do. Live in the home for a full year before any structural remodel — you'll know what actually needs to change.`,
        tip: "👉 [Check when refinancing makes sense](/refi-optimizer) with the break-even calculator.",
      },
      {
        heading: "Months 10–12: The Annual Review",
        content: `Close out the year the way a smart owner does — with a written review.

Home value check. Pull a Zestimate and a comparable sales report. Track equity by subtracting your current mortgage balance from a conservative value estimate. This is the number that quietly makes homeowners wealthier than renters.

Insurance shop. Rates change every year. Get 2–3 quotes at the renewal mark and rebid the policy. Same coverage, often 10–20% less.

Tax prep. Gather Form 1098 (mortgage interest), property tax records, and closing docs. If you itemized, run the numbers both ways this year to confirm it still beats the standard deduction.

Set year-two goals. Emergency fund fully loaded? Extra principal on autopay? One improvement project scoped? Write it down. The homeowners who compound wealth are the ones who plan the next year on purpose.`,
        tip: "👉 [Track your equity and next moves](/homeowner-checkup) with the Homeowner Checkup.",
      },
    ],
    faqs: [
      {
        question: "What's the first thing I should do after closing on my home?",
        answer: "Rekey every exterior lock, transfer utilities into your name, and locate the main water and gas shutoffs. Then scan every closing document into cloud storage — you'll need them at tax time and every future refinance.",
      },
      {
        question: "How much should I budget for home maintenance in year one?",
        answer: "Plan on 1–2% of the home's value per year. On a $400,000 home, that's $4,000–$8,000. Some years you'll spend less, but that reserve prevents a broken furnace from becoming a credit card emergency.",
      },
      {
        question: "When can I refinance my mortgage?",
        answer: "Most loans allow refinancing after 6 months. It generally makes sense when current rates are 0.75% or more below your rate AND you plan to stay in the home long enough to recoup the closing costs — usually 2–4 years.",
      },
      {
        question: "What is a homestead exemption and how do I file it?",
        answer: "A homestead exemption lowers your property tax bill on your primary residence, typically 10–25%. Filing rules and deadlines vary by state — search '[your state] homestead exemption' and file with the county assessor. It's usually a one-page form and free.",
      },
      {
        question: "Which home improvements actually add value?",
        answer: "Kitchen updates, bathroom refreshes, and curb appeal reliably return 60–80% at resale. Elaborate landscaping, pools, and over-personalized finishes rarely pay back. Live in the home for at least a year before any major remodel.",
      },
    ],
  },
  {
    slug: "credit-and-down-payment-runway",
    title: "The Credit & Down Payment Runway Guide",
    subtitle:
      "The 12- to 24-month plan to raise your score, stack your savings, and step into your first mortgage with leverage.",
    author: "Home Approach Team",
    publishDate: "July 2026",
    readTime: "30 min read",
    category: "Finances",
    heroImage: creditRunwayImg,
    freePreviewSections: 3,
    ctaHeadline: "Unlock the Full Runway Plan",
    ctaDescription:
      "Get the full 12- to 24-month credit and savings plan, plus every automation template — free.",
    sections: [
      {
        heading: "The Runway Mindset: Two Numbers That Decide Everything",
        image: creditRunwayImg,
        imageAlt: "Wooden letter tiles spelling out the word CREDIT",
        content: `Most first-time buyers wait too long, then rush. The runway approach flips that: give yourself 12–24 months, and use the time to move two numbers that decide every mortgage offer you'll ever get.

Number one: your credit score. A 740 borrower pays tens of thousands less over a 30-year loan than a 680 borrower on the same house. Every 20-point band matters.

Number two: liquid cash for the deal — down payment, closing costs, reserves, and moving. Not retirement money, not equity in a car. Cash that's ready.

Everything in this guide is a lever on one of those two numbers. If a habit doesn't move either, cut it.`,
        tip: "Pre-approval is what a lender will allow. Readiness is whether the payment still works when life happens. Runway is how you build the second one.",
      },
      {
        heading: "Credit Score Levers Ranked by Impact",
        content: `Not every credit tip carries the same weight. Rank order for a homebuyer:

1. Utilization (30% of your score). Get every revolving card below 10% of its limit — ideally, but not zero. If you carry a $2,000 balance on a $5,000 limit card, dropping it below $500 can move your score 20–40 points in a single reporting cycle.

2. On-time payments (35%). One 30-day late payment can drop a good score 60–100 points and stays for 7 years. Automate minimums on every account. Pay the balance separately.

3. Age of accounts (15%). Never close your oldest card, even if you barely use it. Set a $10 autopay charge on it so the issuer doesn't close it for inactivity.

4. Hard inquiries (10%). Every application dings you 3–5 points. Stop applying for anything new 6–12 months before you plan to apply for a mortgage.

5. Credit mix (10%). Having both revolving (cards) and installment (auto, student loan) helps, but it's not worth taking on debt to manufacture.`,
        tip: "👉 [Check your real score for $3](/check-my-score) before you start — you can't improve what you can't measure.",
      },
      {
        heading: "Common Credit Killers Right Before Closing",
        content: `Even buyers with strong runways trip in the last 60 days. Every one of these is preventable:

Opening a new credit card. Reduces your average account age and adds an inquiry. Wait until after closing.

Financing furniture or appliances. Retail store credit at 0% APR still shows as new debt. Underwriters re-pull credit days before closing. A new $3,000 line can retrigger the entire approval.

Big cash deposits. Anything the underwriter can't source with a paper trail is treated as a gift or a loan. Document every deposit over $500 with a screenshot and note.

Changing jobs. Even a raise at a new company can pause underwriting for 30+ days while your income is re-verified. Wait until after closing when possible.

Missing a single bill. One 30-day late in the underwriting window can kill the approval outright.`,
      },
      {
        heading: "The Savings Stack: Down Payment, Closing, Reserves, Move",
        content: `Down payment is the number people talk about. The full cash need is bigger. Plan for four buckets:

Down payment: 3–20% of the purchase price depending on loan type. FHA allows 3.5% down at a 580 score. Conventional starts at 3%. VA and USDA can hit 0% for eligible buyers.

Closing costs: 2–5% of the loan. On a $400,000 loan, budget $8,000–$20,000. Some can be negotiated onto the seller in slower markets.

Reserves: lenders like to see 2 months of PITI (principal + interest + taxes + insurance) in the bank at close. Your own budget should target 3–6 months.

Move + settle: $1,500–$5,000 depending on distance, furniture, and immediate repairs.

On a $400,000 purchase with 5% down: roughly $20K down + $12K closing + $8K reserves + $3K move = ~$43K liquid.`,
        tip: "👉 [Model your full cash need with our Down Payment Planner](/down-payment-planner) — it accounts for all four buckets, not just the down payment.",
      },
      {
        heading: "Stack Every Dollar of Assistance You Qualify For",
        content: `Down payment assistance is the single most under-used lever in first-time homebuying. Programs stack across four layers:

Federal: FHA, VA, USDA — all provide loan-level benefits. First-time buyer tax credits reappear in some years — check the current federal budget.

State: nearly every state housing agency offers grants or forgivable second mortgages in the $5,000–$25,000 range. Rules vary by income and county.

County + city: local programs often stack on top of state programs. A first-time buyer in a mid-sized metro can frequently combine $10K state + $5K county + $3K city.

Employer: many hospitals, universities, and municipalities offer employer-assisted housing benefits for staff. Public-service professionals — teachers, first responders, healthcare — often qualify for extra rewards on top of grants.`,
        tip: "👉 [Run your Stack](/grant-finder) to see federal, state, county, and employer programs matched to your ZIP and profession.",
      },
      {
        heading: "Automations That Do the Work for You",
        content: `The buyers who hit their number aren't more disciplined. They're better automated.

High-yield savings account (HYSA). Open a dedicated homebuying account at a bank that pays 4%+ APY. Never mix it with checking.

Split direct deposit. Route 5–20% of every paycheck straight to the HYSA before it touches checking. If it never lands in checking, you never miss it.

Windfall rule. Tax refunds, bonuses, and gifts default to the HYSA. Nothing gets rerouted without a conscious decision.

Autopay on every credit line. Minimums at least, ideally full statement. This eliminates the single biggest score killer — the accidental late.

Monthly credit check. Free through Credit Karma, Experian, or your card issuer. Look at it on the first of every month. Trends matter more than any single number.`,
        tip: "👉 [Set your savings goal and track progress](/down-payment-planner) with a monthly milestone.",
      },
      {
        heading: "Your 90-Day Pre-Approval Sprint",
        content: `Once your credit and cash are in shape, the last 90 days is a sprint. Do it in order:

Days 1–30: shop 3–5 lenders. Rates and fees vary by 0.25–0.75% between lenders on the same file — that's tens of thousands of dollars over the loan. Get Loan Estimates (a standardized form) and compare line by line.

Days 30–60: get fully pre-approved (not pre-qualified) with your chosen lender. Pull together W-2s, 1099s, two months of bank statements, two years of tax returns, and government ID. Keep every doc in one folder.

Days 60–90: freeze your financial life. No new credit, no big purchases, no job changes if avoidable. Watch your accounts. Answer every underwriter email within 24 hours.

Then you shop. With a real pre-approval, sellers take you seriously and you close on a schedule you control.`,
        tip: "👉 [Compare lenders with confidence](/lender-match) — we surface the ones that specialize in your situation.",
      },
    ],
    faqs: [
      {
        question: "How long does it take to raise my credit score for a mortgage?",
        answer: "Utilization changes can move your score 20–40 points within one reporting cycle (30–45 days). Bigger changes — clearing collections, aging out late payments — take 6–24 months. Give yourself a 12-month runway if you can.",
      },
      {
        question: "How much cash do I actually need to buy my first home?",
        answer: "Plan for down payment (3–20%), closing costs (2–5% of the loan), 2–3 months of reserves, and $1,500–$5,000 for the move. On a $400,000 purchase with 5% down, expect roughly $40,000–$45,000 in liquid cash needed.",
      },
      {
        question: "Can I combine multiple down payment assistance programs?",
        answer: "Yes. Federal, state, county, city, and employer programs frequently stack. Many first-time buyers combine $10K state + $5K county + employer benefits on top of an FHA loan. Our Stack tool matches every layer you qualify for.",
      },
      {
        question: "What credit score gets the best mortgage rate?",
        answer: "740+ typically unlocks the best conventional rates. 620 is the floor for most conventional loans; 580 for FHA with 3.5% down; 500 for FHA with 10% down. Every 20-point band above 620 usually earns a rate improvement.",
      },
      {
        question: "What should I NOT do right before applying for a mortgage?",
        answer: "Do not open new credit, finance furniture or appliances, make large unexplained deposits, change jobs, or miss any bill. Lenders re-pull credit and re-verify employment days before closing — any of these can derail the approval.",
      },
    ],
  },
];

export const getGuideBySlug = (slug: string): Guide | undefined =>
  guides.find((g) => g.slug === slug);
